// VERA-825: Tag shop checkout API service.
// Communicates with the Architecture T checkout endpoints on the API.

import {api} from './index';
import {TAG_SHOP_MOCK_ENABLED, mockCalculateResult} from 'mocks/tagShopMock';

export interface TagShopLineItem {
  /** "<shopifyProductId>#<variantId>" */
  productId: string;
  variantId: string;
  quantity: number;
  /** True when this line has a custom design — server adds a flat setup fee. */
  customized?: boolean;
  /** Full design (logo data URLs + placement + color/finish). The server uploads
   *  the logos and persists the design on the order as line-item properties. */
  customization?: unknown;
}

export interface TagShopShippingAddress {
  address1: string;
  city: string;
  /** State / province */
  province: string;
  country: string;
  zip: string;
  firstName?: string;
  lastName?: string;
  /** Saved-address label (e.g. "HQ") — persisted on the order. */
  label?: string;
}

export interface TagShopShippingRate {
  handle: string;
  title: string;
  price: number;
}

export interface TagShopCalculateResult {
  subtotalPrice: number;
  totalTax: number;
  totalShippingPrice: number;
  totalPrice: number;
  availableShippingRates: TagShopShippingRate[];
}

export interface TagShopPaymentIntentResult {
  clientSecret: string;
  status: string;
  amount: number;
  reconciliationId: string;
}

export const tagShopApi = {
  /**
   * Call before card entry to get Shopify-authoritative tax + shipping rates.
   */
  calculate: async (params: {
    companyId: string;
    items: TagShopLineItem[];
    shippingAddress: TagShopShippingAddress;
  }): Promise<TagShopCalculateResult> => {
    // DEV-only mock (VITE_TAG_SHOP_MOCK) — stand in for Shopify draftOrderCalculate.
    if (TAG_SHOP_MOCK_ENABLED) {
      return mockCalculateResult;
    }

    const response = await api.post('/payments/tag-shop/calculate', {
      company_id: params.companyId,
      items: params.items,
      shipping_address: params.shippingAddress,
    });
    return response.data?.data;
  },

  /**
   * Create a Stripe PaymentIntent for the tag shop order.
   * Returns a client_secret to confirm with Stripe Elements.
   */
  createPaymentIntent: async (params: {
    companyId: string;
    items: TagShopLineItem[];
    shippingAddress: TagShopShippingAddress;
    shippingRateHandle: string;
  }): Promise<TagShopPaymentIntentResult> => {
    // DEV-only mock (VITE_TAG_SHOP_MOCK). Uses a pre-created Stripe TEST-mode
    // PaymentIntent client_secret (passed via VITE_TAG_SHOP_MOCK_CLIENT_SECRET)
    // so the real Stripe PaymentElement mounts and a 4242 test card completes.
    if (TAG_SHOP_MOCK_ENABLED) {
      const cs = import.meta.env.VITE_TAG_SHOP_MOCK_CLIENT_SECRET as
        | string
        | undefined;
      return {
        clientSecret: cs ?? '',
        status: 'requires_payment_method',
        amount: mockCalculateResult.totalPrice,
        reconciliationId: 'mock-reconciliation',
      };
    }

    const response = await api.post(
      '/payments/tag-shop/create-payment-intent',
      {
        company_id: params.companyId,
        items: params.items,
        shipping_address: params.shippingAddress,
        shipping_rate_handle: params.shippingRateHandle,
      },
    );
    return response.data?.data;
  },
};
